import { Button, ButtonGroup, Card, Header, Segment } from 'semantic-ui-react';
import Alert from '../../../../../utils/alert';
import CloneUtils from '../../../../../utils/cloneUtils';
import ImportsRepo from '../../../../../utils/repository/importsRepo';
import {
    COLUMN_TYPE,
    IMPORT_DESCRIPTOR_TYPE,
    ROW_MAPPING_TARGET,
    ROW_MAPPING_TYPE,
    TEMPORARY_DESCRIPTOR_ID_PREFIX,
    TEMPORARY_ROW_ID_PREFIX,
} from '../../constants/generalConstants';
import useModalState from '../../../../../hooks/useModalState';
import useMappingStore from '../../stores/mappingStore';
import {
    AlternativeType,
    ExtractionType,
    MappingsType,
    MasterDescriptorsMapType,
    ResponseStatusType,
    RowIdType,
    RowMappingType,
} from '../../types';
import { ensureEmptyMapping, getEmptyMapping, getIsMappingEmpty } from './utils';
import MappingsSaveModal from './components/mappingsSaveModal';
import MappingsTable from './components/mappingsTable';
import { MASTER_DESCRIPTOR_OVER_PREFIX } from './constants';
import { useState } from 'react';
import { RESPONSE_STATUS } from '../../../../../constnats/general';
import BrowserStorage from '../../../../../utils/browserStorage';
import RemovedMappings from './components/removedMappings';
import { useMetadataStoreState } from '../../stores/metadataStore';
import Assert from '../../../../../utils/asserts';

type StrategyHandlerType = (
    sourceIndex: number,
    targetIndex: number,
    source: RowMappingType,
    target: RowMappingType,
    mappings: MappingsType
) => void;

type MappingsPropsType = {
    extraction: ExtractionType;
    handleDeleteImport: () => void;
    setExtraction: (extraction: ExtractionType) => void;
    masterDescriptorsMap: MasterDescriptorsMapType;
    setGlobalLoading: (loading: boolean) => void;
};
export default function Mappings({
    extraction,
    handleDeleteImport,
    setExtraction,
    masterDescriptorsMap,
    setGlobalLoading,
}: MappingsPropsType) {
    const { rowsMetadata, colsMetadata, numberFormat, scale, financialSource } = useMetadataStoreState();
    const {
        confidencesMap,
        draggedItemId,
        mappings,
        draggedOverItemId,
        removedMappings,
        initiallyEmptyMasterDescriptors,
        setDraggedItemType,
        setRemovedMappings,
        setDraggedItemId,
        setMappings,
        setDraggedOverItemId,
        setConfidencesMap,
    } = useMappingStore();
    const saveModalState = useModalState();
    const [loading, setLoading] = useState(false);

    const handleSetLoading = (loadingState: boolean) => {
        setLoading(loadingState);
        setGlobalLoading(loadingState);
    };

    const resetDraggableState = () => {
        setDraggedItemType(null);
        setDraggedItemId(undefined);
        setDraggedOverItemId(undefined);
    };

    const handleImportDescriptorOnDropSwap = () => {
        if (!draggedOverItemId || !draggedItemId) {
            resetDraggableState();
            return;
        }

        const source = mappings.find((mapping) => mapping.rowId === draggedItemId);
        if (!source) {
            resetDraggableState();
            return;
        }

        const target = mappings.find((mapping) => mapping.descriptorId === draggedOverItemId);
        if (!target) {
            resetDraggableState();
            return;
        }
        const sourceDescriptor = extraction.importTable.descriptors.find((d) => d.id === source.rowId);
        const targetDescriptor = extraction.importTable.descriptors.find(
            (d) => d.id === target.rowId || d.masterDescriptorId === target.descriptorId
        );

        if (
            (sourceDescriptor?.dataType === IMPORT_DESCRIPTOR_TYPE.HEADLINE &&
                targetDescriptor?.dataType !== IMPORT_DESCRIPTOR_TYPE.HEADLINE) ||
            (sourceDescriptor?.dataType !== IMPORT_DESCRIPTOR_TYPE.HEADLINE &&
                targetDescriptor?.dataType === IMPORT_DESCRIPTOR_TYPE.HEADLINE)
        ) {
            Alert.warn('Cannot swap Headline with Non-Headline field');
            resetDraggableState();
            return;
        }

        let newMappings = mappings.map((mapping) => {
            if (mapping.rowId === draggedItemId) {
                // source case
                const confidence =
                    initiallyEmptyMasterDescriptors.includes(mapping.descriptorId) &&
                    target.rowId.startsWith(TEMPORARY_ROW_ID_PREFIX)
                        ? 100
                        : confidencesMap[target.rowId]?.[source.descriptorId] ?? 0;
                return {
                    ...source,
                    rowId: target.rowId,
                    alternatives: CloneUtils.deepClone(target.alternatives),
                    confidence,
                };
            } else if (mapping.descriptorId === draggedOverItemId) {
                // target case
                return {
                    ...target,
                    rowId: source.rowId,
                    alternatives: CloneUtils.deepClone(source.alternatives),
                    confidence: confidencesMap[source.rowId]?.[target.descriptorId] ?? 0,
                };
            } else {
                return mapping;
            }
        });

        newMappings = newMappings.filter((mapping) => !getIsMappingEmpty(mapping));
        ensureEmptyMapping(newMappings);
        setMappings(newMappings);
        resetDraggableState();
    };

    /**
     *
     * @param strategyHandler Executes the mapping strategy and MUTATES the passed collection
     */
    const baseMasterDescriptorStrategyHandler = (strategyHandler: StrategyHandlerType) => {
        if (!draggedOverItemId || !draggedItemId) {
            return;
        }

        const source = mappings.find((mapping) => mapping.descriptorId === draggedItemId);
        if (!source) {
            resetDraggableState();
            return;
        }

        const target = mappings.find(
            (mapping) => mapping.descriptorId === draggedOverItemId.replace(MASTER_DESCRIPTOR_OVER_PREFIX, '')
        );
        if (!target) {
            resetDraggableState();
            return;
        }

        const sourceIndex = mappings.findIndex((mapping) => mapping.descriptorId === source.descriptorId);
        if (sourceIndex < 0) {
            resetDraggableState();
            return;
        }

        const targetIndex = mappings.findIndex((mapping) => mapping.descriptorId === target.descriptorId);
        if (targetIndex < 0) {
            resetDraggableState();
            return;
        }

        const newMappings: MappingsType = CloneUtils.deepClone(mappings);

        strategyHandler(sourceIndex, targetIndex, source, target, newMappings);

        setMappings(newMappings);
        resetDraggableState();
    };

    const handleMasterDescriptorOnDropSwap = () => {
        baseMasterDescriptorStrategyHandler((sourceIndex, targetIndex, source, target, mappings) => {
            mappings[sourceIndex] = target;
            mappings[targetIndex] = source;
        });
    };

    const handleMasterDescriptorOnDropInsert = () => {
        baseMasterDescriptorStrategyHandler((sourceIndex, targetIndex, source, target, mappings) => {
            mappings.splice(sourceIndex, 1);
            const insertIndex = sourceIndex < targetIndex ? targetIndex : targetIndex + 1;
            mappings.splice(insertIndex, 0, source);
        });
    };

    const handleMappingsSave = () => {
        handleSetLoading(true);
        saveModalState.closeModal();
        const mappingsForUpdate = mappings
            .map((x) => {
                const rowId = x.rowId.includes(TEMPORARY_ROW_ID_PREFIX) ? null : x.rowId;
                const descriptorId = x.descriptorId.includes(TEMPORARY_DESCRIPTOR_ID_PREFIX) ? null : x.descriptorId;
                const alternatives = x.alternatives.filter(
                    (x) => x.target !== ROW_MAPPING_TARGET.SELF && x.type !== ROW_MAPPING_TYPE.INITIAL
                );
                return {
                    ...x,
                    descriptorId,
                    rowId,
                    alternatives,
                };
            })
            .filter((x) => x.rowId !== null || x.descriptorId !== null);

        const save = () => {
            ImportsRepo.mappingsUpdate(
                extraction.id,
                { mappings: mappingsForUpdate },
                (res: { status: ResponseStatusType; data: ExtractionType; message: string }) => {
                    if (res.status === RESPONSE_STATUS.SUCCESS) {
                        setRemovedMappings([]);
                        setExtraction(res.data);
                        Alert.success('Mappings successfully saved');
                        BrowserStorage.setLabelsNormalizationStatus(extraction.id, false);
                    } else {
                        Alert.error(res.message);
                    }
                    handleSetLoading(false);
                }
            );
        };

        if (removedMappings.length) {
            const colsMetadataToSave = colsMetadata.map((colMetadata) => {
                if (colMetadata.contentType !== COLUMN_TYPE.REPORT) {
                    const { reportProps, ...rest } = colMetadata;
                    return {
                        ...rest,
                    };
                } else {
                    return colMetadata;
                }
            });

            const rowsMetadataToSave = rowsMetadata.map((rowMetadata) => {
                if (removedMappings.find((m) => m.rowId === rowMetadata.rowId)) {
                    return { ...rowMetadata, importCandidate: false };
                } else {
                    return rowMetadata;
                }
            });

            ImportsRepo.metadataUpdate(
                extraction.id,
                {
                    colAnnotations: colsMetadataToSave,
                    rowAnnotations: rowsMetadataToSave,
                    numberFormat,
                    scale,
                    financialSource,
                },
                (response: any) => {
                    if (response.status === RESPONSE_STATUS.SUCCESS) {
                        Alert.success('Metadata successfully saved');
                        save();
                    } else {
                        handleSetLoading(false);
                        Alert.error(`ERROR: ${response.message}`);
                    }
                }
            );
        } else {
            save();
        }
    };

    const handleRemoveImportDescriptor = (rowId: RowIdType) => {
        const newConfidencesMap = CloneUtils.deepClone(confidencesMap);
        const newRemovedMappings = CloneUtils.deepClone(removedMappings);
        const newMappings = mappings
            .map((mapping) => {
                if (mapping.rowId === rowId) {
                    const emptyMapping = getEmptyMapping();
                    const deletedMapping = CloneUtils.deepClone(mapping);
                    deletedMapping.descriptorId = emptyMapping.descriptorId;
                    deletedMapping.confidence = 0;
                    newRemovedMappings.push(deletedMapping);
                    newConfidencesMap[emptyMapping.rowId] = { [mapping.descriptorId]: emptyMapping.confidence };
                    return {
                        ...emptyMapping,
                        alternatives: emptyMapping.alternatives.map((alt) => {
                            if (alt.target === ROW_MAPPING_TARGET.SELF && alt.type === ROW_MAPPING_TYPE.INITIAL) {
                                return { ...alt, descriptorId: mapping.descriptorId };
                            } else {
                                return alt;
                            }
                        }),
                        descriptorId: mapping.descriptorId,
                    };
                } else {
                    return mapping;
                }
            })
            .filter((mapping) => !getIsMappingEmpty(mapping));
        setConfidencesMap(newConfidencesMap);
        ensureEmptyMapping(newMappings);
        setMappings(newMappings);
        setRemovedMappings(newRemovedMappings);
    };

    const handleRestoreImportDescriptor = (mapping: RowMappingType) => {
        const initialMapping = mapping.alternatives.find(
            (alt) => alt.target === ROW_MAPPING_TARGET.SELF && alt.type === ROW_MAPPING_TYPE.INITIAL
        );
        Assert.trueVal(!!initialMapping, 'Could not find initialMapping in mapping restore');
        const isInitialFree = mappings.some(
            (m) => m.descriptorId === initialMapping?.descriptorId && m.rowId.startsWith(TEMPORARY_ROW_ID_PREFIX)
        );
        if (isInitialFree && initialMapping) {
            setMappings(
                mappings.map((m) => {
                    if (m.descriptorId === initialMapping.descriptorId) {
                        return {
                            ...mapping,
                            confidence: initialMapping.confidence,
                            descriptorId: initialMapping.descriptorId,
                        };
                    } else {
                        return m;
                    }
                })
            );
        } else {
            const confidence = initialMapping?.descriptorId.startsWith(TEMPORARY_DESCRIPTOR_ID_PREFIX)
                ? 100
                : mapping.confidence;
            setMappings([...mappings, { ...mapping, confidence }]);
        }
        setRemovedMappings(removedMappings.filter((m) => m.rowId !== mapping.rowId));
    };

    return (
        <Segment>
            <Segment>
                <div className="overflow--auto">
                    <MappingsTable
                        handleRemoveImportDescriptor={handleRemoveImportDescriptor}
                        handleImportDescriptorOnDropSwap={handleImportDescriptorOnDropSwap}
                        handleMasterDescriptorOnDropInsert={handleMasterDescriptorOnDropInsert}
                        handleMasterDescriptorOnDropSwap={handleMasterDescriptorOnDropSwap}
                        importDescriptors={extraction.importTable.descriptors}
                        masterDescriptorsMap={masterDescriptorsMap}
                        reports={extraction.importTable.reports}
                    />
                </div>
            </Segment>

            <ButtonGroup fluid>
                <Button disabled={loading} loading={loading} primary onClick={saveModalState.openModal}>
                    Save
                </Button>
                <Button negative onClick={handleDeleteImport}>
                    Delete
                </Button>
            </ButtonGroup>
            <MappingsSaveModal
                open={saveModalState.open}
                onClose={saveModalState.closeModal}
                handleSave={handleMappingsSave}
            />
            {!!removedMappings.length && (
                <Segment>
                    <Header as={'h4'}>Removed Rows</Header>
                    <RemovedMappings
                        mappings={removedMappings}
                        importDescriptors={extraction.importTable.descriptors}
                        handleRestoreImportDescriptor={handleRestoreImportDescriptor}
                    />
                </Segment>
            )}
        </Segment>
    );
}
